import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/useAuth";
import { Masthead } from "../components/Masthead";
import { IconSpinner } from "../components/Icons";
import { PasswordField } from "../components/PasswordField";
import type { AuthUser, UserRole } from "../types";

interface InviteInfo {
  email: string;
  displayName: string | null;
  role: UserRole;
  clientName: string | null;
  expiresAt: string;
}

function roleLabel(role: UserRole): string {
  if (role === "valuation_analyst") return "Valuation Analyst";
  if (role === "admin") return "Administrator";
  return "Client";
}

export default function AcceptInvite() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<InviteInfo>(`/api/invite/${token}`);
        if (cancelled) return;
        setInfo(data);
        setDisplayName(data.displayName ?? "");
      } catch (err) {
        if (cancelled) return;
        setLoadErr(err instanceof ApiError ? err.message : "Could not load invite");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormErr(null);
    if (password.length < 8) {
      setFormErr("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setFormErr("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { user } = await api.post<{ user: AuthUser }>("/api/accept-invite", {
        token,
        password,
        displayName: displayName.trim() || undefined,
      });
      setUser(user);
      navigate(user.role === "client" ? "/c/me" : "/admin", { replace: true });
    } catch (err) {
      setFormErr(err instanceof ApiError ? err.message : "Failed to accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadErr) {
    return (
      <main className="page-narrow">
        <Masthead as="div" />
        <h2 style={{ fontSize: "var(--text-xl)", fontWeight: 600, marginTop: "var(--space-3)" }}>
          Invite unavailable
        </h2>
        <p className="text-muted" style={{ marginTop: "var(--space-3)" }}>{loadErr}</p>
        <p className="text-subtle" style={{ marginTop: "var(--space-3)", fontSize: "var(--text-sm)" }}>
          Contact whoever invited you to request a fresh link.
        </p>
      </main>
    );
  }

  if (!info) {
    return (
      <main className="page-narrow" style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
        <IconSpinner size={20} aria-label="Loading invite" />
      </main>
    );
  }

  return (
    <main className="page-narrow">
      <Masthead as="div" />

      <div style={{ marginTop: "var(--space-2)", marginBottom: "var(--space-5)" }}>
        <span className="sc sc-accent">Invitation</span>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 600,
            fontSize: "var(--text-2xl)",
            marginTop: "var(--space-2)",
            letterSpacing: "var(--track-display)",
            color: "var(--ink)",
            lineHeight: 1.15,
          }}
        >
          {info.clientName ? `Welcome to ${info.clientName}.` : "Welcome."}
        </h2>
        <p className="text-muted" style={{ marginTop: "var(--space-3)", lineHeight: "var(--lh-normal)" }}>
          You've been invited to CRE Property KB as <strong className="text-ink">{info.email}</strong>
          {" with the role of "}
          <strong className="text-ink">{roleLabel(info.role)}</strong>. Set your password below.
        </p>
      </div>

      {formErr && (
        <div className="alert alert-error" role="alert">
          {formErr}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="accept-name">Display name</label>
          <input
            id="accept-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="accept-pw">Password</label>
          <PasswordField
            id="accept-pw"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="form-hint">Minimum 8 characters.</div>
        </div>

        <div className="form-group">
          <label htmlFor="accept-pw2">Confirm password</label>
          <PasswordField
            id="accept-pw2"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting && <IconSpinner size={14} />}
            {submitting ? "Setting password…" : "Accept invitation"}
          </button>
        </div>
      </form>

      <p className="disclaim-line">
        <strong>This is not an appraisal.</strong> It is a valuation analysis for commercial real estate.
      </p>
    </main>
  );
}
